import { Link } from "react-router-dom";
import { CATEGORIES } from "@/lib/categories";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Seo, breadcrumbLd, SITE_URL, SITE_NAME } from "@/components/Seo";
import { BookOpen, ListChecks } from "lucide-react";

const A = ({ to, children }) => (
  <Link to={to} className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2 hover:text-gold-soft transition-colors">
    {children}
  </Link>
);

const TOC = [
  ["cos-e", "Cos'è il collezionismo (e perché è anche un mercato)"],
  ["scegliere", "1. Scegli un ambito e restringilo"],
  ["budget", "2. Quanto budget serve davvero"],
  ["conservazione", "3. La conservazione vale più della rarità"],
  ["dove-comprare", "4. Dove si compra e dove si rischia"],
  ["valutare", "5. Come si valuta un oggetto"],
  ["grading", "6. Grading e certificazioni: quando servono"],
  ["custodia", "7. Custodire, documentare, assicurare"],
  ["errori", "8. Gli otto errori dei principianti"],
  ["categorie", "9. Da dove partire, categoria per categoria"],
  ["faq", "Domande frequenti"],
];

const FAQ = [
  ["Cosa significa collezionismo?",
    "Collezionismo è la raccolta intenzionale e ordinata di oggetti legati da un tema: una serie di monete, le carte di un set, gli orologi di una referenza. Diventa anche un mercato quando quegli oggetti hanno una domanda stabile e prezzi confrontabili tra esemplari simili."],
  ["Quali sono i settori più collezionati oggi?",
    "In Italia i più attivi sono le carte da gioco (Pokémon e TCG), gli orologi vintage, le auto d'epoca e le youngtimer, la numismatica, la filatelia, i fumetti d'epoca, le sneakers, i set LEGO ritirati, l'arte grafica e i memorabilia sportivi."],
  ["Con quanti soldi si può iniziare a collezionare?",
    "Si può iniziare con poche decine di euro: francobolli, monete circolate e fumetti recenti costano pochissimo. La spesa vera arriva quando si cercano esemplari in alta conservazione: meglio un oggetto buono che cinque mediocri."],
  ["Come si capisce quanto vale un oggetto da collezione?",
    "Si guardano i prezzi realizzati da esemplari simili nello stesso stato di conservazione, non i listini o i prezzi richiesti. Le nostre Schede Valore mostrano fasce indicative con i fattori che le spostano, ma per una vendita importante serve una perizia."],
  ["Il collezionismo è un investimento?",
    "Non va trattato come tale: è illiquido (vendere può richiedere mesi), ha costi di transazione alti e i prezzi possono scendere. Colleziona ciò che ti piace guardare; l'eventuale guadagno è un effetto collaterale, non l'obiettivo."],
  ["Conviene far certificare i propri pezzi?",
    "Conviene quando il costo della certificazione è una frazione piccola del valore dell'oggetto e quando il mercato di riferimento compra prevalentemente pezzi certificati, come accade per carte, monete e fumetti di pregio."],
];

export default function PillarGuidePage() {
  return (
    <article className="max-w-3xl mx-auto px-4 py-12 fade-up" data-testid="pillar-guide-page">
      <Seo
        title="Collezionismo: la guida completa per iniziare"
        description="Cos'è il collezionismo, come scegliere un ambito, quanto budget serve, come valutare e conservare i pezzi, dove comprare senza rischi e gli errori da evitare. Guida pratica in italiano."
        path="/collezionismo"
        type="article"
        jsonLd={[
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Collezionismo: guida completa", path: "/collezionismo" }]),
          {
            "@context": "https://schema.org", "@type": "Article",
            headline: "Collezionismo: la guida completa per iniziare",
            description: "Guida pratica al collezionismo: scegliere un ambito, budget, conservazione, valutazione, grading, custodia ed errori da evitare.",
            inLanguage: "it-IT",
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/collezionismo` },
            author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          },
          {
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: FAQ.map(([q, a]) => ({
              "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
        ]}
      />

      <div className="flex items-center gap-2">
        <BookOpen size={15} className="text-gold" />
        <span className="font-meta text-[10px] uppercase tracking-[0.24em] text-stone-500">Guida pilastro</span>
      </div>
      <h1 className="mt-4 font-editorial font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
        <span className="mark-hl">Collezionismo</span>: la guida completa per iniziare
      </h1>
      <p className="mt-6 text-lg text-stone-600 leading-relaxed">
        Collezionare è una passione che si impara: capire cosa rende un oggetto desiderabile, quanto pesa lo stato
        di conservazione, dove comprare senza farsi male e quando conviene fermarsi. Questa guida raccoglie le
        regole che valgono in ogni ambito — dalle carte Pokémon alle auto d'epoca — e ti indica da dove partire.
      </p>

      <nav className="mt-9 rounded-2xl bg-panel border-2 border-line p-5" data-testid="pillar-toc">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks size={14} className="text-gold" />
          <span className="font-meta text-[10px] uppercase tracking-[0.2em] text-stone-500">In questa guida</span>
        </div>
        <ol className="space-y-1.5">
          {TOC.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`} className="text-sm text-stone-600 hover:text-stone-900 transition-colors">{label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-12 space-y-11 article-body">
        <section id="cos-e">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Cos'è il collezionismo (e perché è anche un mercato)</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Il collezionismo è la raccolta intenzionale di oggetti legati da un filo: una serie completa, un
            periodo storico, un autore, una referenza. La differenza tra accumulare e collezionare sta proprio in
            quel filo: una collezione ha un criterio, e il criterio è ciò che le dà senso — e, col tempo, valore.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Dagli anni Duemila molti settori hanno sviluppato un mercato vero, con prezzi pubblici, esemplari
            confrontabili e indici di riferimento. Questo è un vantaggio, perché rende trasparente ciò che prima
            si sapeva solo frequentando i negozi, ma introduce anche un rischio: trattare la passione come un
            investimento. Sono due cose diverse, e più avanti vedremo perché è meglio non confonderle.
          </p>
        </section>

        <section id="scegliere">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">1. Scegli un ambito e restringilo</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            L'errore numero uno è iniziare "a tutto campo". Una collezione senza confini diventa un mucchio di
            oggetti scollegati, difficili da completare e da rivendere. Restringi: non "monete", ma "monete del
            Regno d'Italia da 20 lire"; non "orologi", ma "cronografi meccanici anni Sessanta"; non "LEGO", ma
            "set Star Wars UCS".
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Un perimetro stretto ti fa diventare competente in fretta: inizi a riconoscere a colpo d'occhio le
            varianti, i falsi, i prezzi fuori mercato. È questa competenza — non il budget — la vera differenza
            tra chi compra bene e chi compra male.
          </p>
        </section>

        <section id="budget">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">2. Quanto budget serve davvero</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Si può iniziare con pochissimo: francobolli e monete circolate costano cifre simboliche, i fumetti
            degli anni Novanta si trovano a pochi euro. La spesa cresce quando si cercano esemplari in alta
            conservazione, e lì vale una regola che tutti i collezionisti esperti ripetono: <strong className="text-stone-800">meglio
            un pezzo buono che cinque mediocri</strong>. Gli oggetti in condizioni medie sono i primi a perdere
            interesse quando il mercato rallenta.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Metti a bilancio anche i costi accessori, che i principianti dimenticano: spedizioni assicurate,
            dogana sugli acquisti extra-UE, commissioni d'asta (spesso il 20-30% in più sul prezzo di
            aggiudicazione), materiali di conservazione, eventuale certificazione.
          </p>
        </section>

        <section id="conservazione">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">3. La conservazione vale più della rarità</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Sembra controintuitivo, ma tra un pezzo raro in condizioni mediocri e un pezzo comune in condizioni
            eccezionali, spesso è il secondo a spuntare il prezzo migliore in proporzione. Nelle carte da gioco
            la differenza tra un grado 8 e un grado 10 può essere di dieci volte il valore; in numismatica il
            salto tra "bello" e "fior di conio" cambia completamente la cifra; per un'auto d'epoca la
            documentazione originale e la coerenza dei numeri contano più dei chilometri.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Impara quindi il linguaggio della conservazione del tuo ambito prima di comprare: le scale di grado,
            i difetti che si perdonano e quelli che azzerano il valore, le riparazioni accettate e quelle che
            trasformano un pezzo da collezione in un oggetto qualunque.
          </p>
        </section>

        <section id="dove-comprare">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">4. Dove si compra e dove si rischia</h2>
          <ul className="mt-3 space-y-2.5 text-stone-600 leading-relaxed">
            <li><strong className="text-stone-800">Case d'asta specializzate</strong>: massima garanzia di autenticità e descrizioni serie, ma commissioni alte e ritmi da rispettare.</li>
            <li><strong className="text-stone-800">Negozi e dealer di settore</strong>: paghi un margine, però ottieni consulenza, possibilità di restituzione e un rapporto che nel tempo ti fa vedere i pezzi prima degli altri.</li>
            <li><strong className="text-stone-800">Mercatini e fiere</strong>: qui si fanno i colpi migliori e i peggiori. Vai preparato, con prezzi di riferimento già in tasca.</li>
            <li><strong className="text-stone-800">Piattaforme online</strong>: scelta enorme, ma è il territorio dei falsi e delle foto ingannevoli. Pretendi immagini reali e dettagliate, chiedi il numero di serie, verifica lo storico del venditore e paga sempre con metodi tracciabili e protetti.</li>
          </ul>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Regola universale: se un prezzo è troppo buono per essere vero, non è vero. I falsi migliori non si
            riconoscono dalle foto, si riconoscono dal contesto.
          </p>
        </section>

        <section id="valutare">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">5. Come si valuta un oggetto</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Il valore di un oggetto da collezione non sta in un listino: sta nei <strong className="text-stone-800">prezzi
            realizzati</strong> da esemplari confrontabili, cioè venduti davvero, nello stesso stato di
            conservazione, di recente. Guardare i prezzi richiesti dai venditori è il modo più rapido per
            sopravvalutare la propria collezione.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Metodo pratico: prendi almeno cinque vendite concluse degli ultimi mesi, scarta l'estremo più alto e
            quello più basso, considera la media dei restanti e correggila per le differenze concrete (grado,
            completezza, documentazione, provenienza). Nelle nostre{" "}
            <A to="/valori">Schede Valore</A> trovi fasce indicative già impostate così, con l'elenco dei fattori
            che le spostano in alto o in basso.
          </p>
        </section>

        <section id="grading">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">6. Grading e certificazioni: quando servono</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Far certificare un pezzo da un ente terzo (grading) significa farlo valutare, sigillare e garantire
            nell'autenticità e nel grado. Ha senso in tre casi: quando il valore dell'oggetto è molto superiore al
            costo del servizio, quando il mercato di riferimento compra prevalentemente pezzi certificati, quando
            pensi di vendere a distanza a chi non può esaminare l'oggetto.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Non ha senso, invece, su oggetti di valore modesto: la certificazione costa quanto il pezzo e non lo
            rende più desiderabile. E attenzione: la certificazione garantisce autenticità e grado, non il prezzo.
          </p>
        </section>

        <section id="custodia">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">7. Custodire, documentare, assicurare</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            I tre nemici di qualunque collezione sono <strong className="text-stone-800">luce, umidità e sbalzi di
            temperatura</strong>; il quarto è la fretta con cui si maneggiano gli oggetti. Usa materiali neutri e
            certificati (buste senza PVC, fogli senza acidi, contenitori chiusi), tieni la collezione lontana da
            sole diretto e caloriferi, e maneggia con guanti quello che si graffia o si ossida.
          </p>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Documenta tutto in un inventario: foto, descrizione, data e prezzo d'acquisto, fatture e
            certificazioni. Serve a te per capire come stai andando, serve all'assicurazione in caso di furto o
            danno, e servirà a chi eredita la collezione per non svenderla per ignoranza. Oltre una certa soglia
            di valore, verifica la copertura della polizza casa: quasi sempre gli oggetti da collezione
            richiedono una garanzia dedicata con perizia allegata.
          </p>
        </section>

        <section id="errori">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">8. Gli otto errori dei principianti</h2>
          <ol className="mt-3 space-y-2 text-stone-600 leading-relaxed list-decimal pl-5">
            <li>Comprare tanto e male all'inizio, invece di studiare per qualche mese.</li>
            <li>Inseguire la moda del momento quando i prezzi sono già sui massimi.</li>
            <li>Ignorare lo stato di conservazione perché "tanto è raro".</li>
            <li>Pulire, lucidare o restaurare da soli: spesso è il modo più rapido per azzerare il valore.</li>
            <li>Fidarsi di prezzi richiesti anziché di vendite concluse.</li>
            <li>Dimenticare commissioni, spedizioni, dogana e tasse nel conto finale.</li>
            <li>Non conservare scatole, certificati, ricevute e documentazione originale.</li>
            <li>Trattare la collezione come un investimento liquido: vendere bene richiede tempo e il compratore giusto.</li>
          </ol>
        </section>

        <section id="categorie">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">9. Da dove partire, categoria per categoria</h2>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Ogni ambito ha le sue regole, i suoi falsi e i suoi indicatori di prezzo: per questo abbiamo scritto
            una guida dedicata per ciascuno, con budget d'ingresso, riconoscimento dei falsi e conservazione —
            le trovi tutte in <A to="/come-iniziare">come iniziare a collezionare, categoria per categoria</A>.
            Da qui invece entri nelle sezioni con le notizie aggiornate e le fasce di valore:
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/categoria/${c.slug}`} data-testid={`pillar-cat-${c.slug}`}
                className={`rounded-2xl bg-panel border-2 border-line border-l-[6px] ${c.accent} p-4 lift`}>
                <span className="block font-semibold text-sm text-stone-900">{c.name}</span>
                <span className="block text-xs text-stone-500 mt-1 leading-snug">{c.description}</span>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-stone-600 leading-relaxed">
            Se invece vuoi partire dai numeri, le <A to="/valori">Schede Valore</A> rispondono direttamente alla
            domanda "quanto vale": ogni scheda ha la sua pagina con fascia di prezzo, fattori che la spostano e
            andamento nel tempo. Per approfondimenti tematici c'è la sezione <A to="/guide">Guide</A>.
          </p>
        </section>

        <section id="faq">
          <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">Domande frequenti sul collezionismo</h2>
          <div className="mt-4 space-y-3">
            {FAQ.map(([q, a]) => (
              <details key={q} className="rounded-2xl bg-panel border-2 border-line p-4">
                <summary className="cursor-pointer font-semibold text-stone-900 text-sm marker:text-gold">{q}</summary>
                <p className="mt-2.5 text-sm text-stone-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      <p className="mt-12 text-[11px] text-stone-400 leading-relaxed">
        Contenuto informativo e divulgativo: non costituisce consulenza finanziaria né perizia. Le fasce di
        prezzo citate sono indicative. Dettagli nel <A to="/disclaimer">disclaimer</A>.
      </p>

      <div className="mt-10"><NewsletterSignup /></div>
    </article>
  );
}
